import { IconName } from '@portal/shared/ui/icon/icon-name';
import { Permission } from '@portal/core/auth/permissions';

/**
 * What the sidebar renders from -- the portal's own.
 *
 * **This lived in `eshava-shared` for a day and came back out on 2026-08-27.** Two reasons, and the
 * first is the one that settles it: `UI.Angular.Assets` has a sidebar and could not have used the
 * shared one. It derives its menu from the **router configuration** -- every route carries `titleKey`,
 * `icon` and `isMainNavigation` in its `data` -- where this application does the opposite and derives
 * its routes from the list below. Two directions of one derivation, not two skins. And its second
 * level is not a sidebar level at all: the children go into a bar inside each screen.
 *
 * The second reason is deliberate freedom: the portal and Sentinel are to stay able to take their
 * sidebars in different directions, and one shared component forbids that. **The cost is stated**
 * rather than hidden -- a change wanted in both is now two changes.
 */

/**
 * One screen inside an area. `path` is relative to the area, so the two concatenate into the route
 * and nothing has to repeat the area name.
 */
export interface NavigationChild {
  readonly path: string;
  readonly titleKey: string;
  /**
   * What the screen needs, **beyond** what its area needs. The two are added together where they are
   * checked, so a screen lists only its own -- repeating the module on every child is how one of them
   * ends up missing it.
   */
  readonly permissions?: readonly Permission[];
}

export interface NavigationEntry {
  /** Route segment, and at the same time the folder name under `features/`. */
  readonly path: string;
  readonly titleKey: string;
  /** A symbol in this application's sprite, typed to it: a typo is a compile error, not an empty square. */
  readonly icon: IconName;
  /**
   * Pinned to the bottom of the sidebar, away from the others -- a statement about the working
   * rhythm rather than about permissions.
   *
   * **Set explicitly rather than derived from `requiresProduct`**, although today the two agree. One
   * flag answering two questions is how they drift apart: this one is about where an entry sits, the
   * other about whether the product dropdown applies.
   */
  readonly pinned?: boolean;
  /**
   * Whether the area works within a product context. Only `settings` does not: subscriptions,
   * products and portal users are account-wide. Read by the top bar, not by the sidebar.
   */
  readonly requiresProduct: boolean;
  /**
   * The module permission the whole area hangs on. Every screen in it needs this as well as its own,
   * which is what makes an area disappear rather than showing a list of refusals.
   */
  readonly permissions: readonly Permission[];
  /**
   * The screens underneath, in the order they are shown. **The first is where the area opens** --
   * the route redirects to it, so an area link never lands on an empty frame.
   *
   * Absent while an area is still a placeholder. Once it has screens it gets them here rather than
   * in its route file, so the sidebar and the routes keep being derived from one list.
   */
  readonly children?: readonly NavigationChild[];
}

/**
 * The five areas, **in the order the sidebar shows them** -- top to bottom. The single place they are
 * listed: the sidebar renders from this and the routes are derived from it, so the two cannot drift
 * apart.
 *
 * The cut follows the data model rather than Core's `applications` / `permissions` split: module,
 * permission, licence and role template all hang off an application, which is why they are three
 * areas instead of one. See `CLAUDE.md`, "Areas".
 *
 * `requiresProduct` does double duty: it decides whether the product dropdown applies, and it
 * decides where an entry sits. Account-wide areas are pinned to the bottom of the sidebar -- they
 * are not part of the working rhythm of the product areas, and separating them says so. Today that
 * is settings alone.
 */
export const NAVIGATION: readonly NavigationEntry[] = [
  {
    path: 'applications',
    titleKey: 'area.applications.title',
    icon: 'cubes-solid',
    requiresProduct: true,
    permissions: [Permission.PORTAL_CAN_USE_MODULE_APPLICATIONS],
    children: [
      {
        path: 'applications',
        titleKey: 'applications.applications.title',
        permissions: [Permission.PORTAL_CAN_USE_APPLICATIONS]
      },
      /* Flat across the product rather than one list per application: expiry is the reason to open
         this screen, and a token hidden inside each application would scatter it. */
      {
        path: 'machine-tokens',
        titleKey: 'applications.machineTokens.title',
        permissions: [Permission.PORTAL_CAN_USE_MACHINE_TOKENS]
      },
      /* A sibling although the name suggests otherwise -- a service hook belongs to the product, not
         to an application. Every one of its endpoints works on `ApplicationTypeId`. */
      {
        path: 'service-hooks',
        titleKey: 'applications.serviceHooks.title',
        permissions: [Permission.PORTAL_CAN_USE_SERVICE_HOOKS]
      }
    ]
  },
  {
    path: 'permissions',
    titleKey: 'area.permissions.title',
    icon: 'shield-halved-solid',
    requiresProduct: true,
    permissions: [Permission.PORTAL_CAN_USE_MODULE_PERMISSIONS],
    children: [
      /* The base tool first: a permission needs no module, and a vendor can build a whole system
         without ever opening the screen below. */
      {
        path: 'permissions',
        titleKey: 'permissions.permissions.title',
        permissions: [Permission.PORTAL_CAN_USE_PERMISSIONS]
      },
      {
        path: 'structure',
        titleKey: 'permissions.structure.title',
        permissions: [Permission.PORTAL_CAN_USE_PERMISSION_STRUCTURE]
      },
      {
        path: 'role-templates',
        titleKey: 'permissions.roleTemplates.title',
        permissions: [Permission.PORTAL_CAN_USE_ROLE_TEMPLATES]
      }
    ]
  },
  {
    path: 'licenses',
    titleKey: 'area.licenses.title',
    icon: 'certificate-solid',
    requiresProduct: true,
    permissions: [Permission.PORTAL_CAN_USE_MODULE_LICENSES],
    /*
     * Two entries, and they carry different permissions: someone may arrange the packages without
     * being allowed to decide what is in them, which is a distinction the permission set already
     * made and only the actions used to show.
     */
    children: [
      { path: 'types', titleKey: 'licenses.types.title', permissions: [Permission.PORTAL_CAN_USE_LICENSE_TYPES] },
      { path: 'licenses', titleKey: 'licenses.title', permissions: [Permission.PORTAL_CAN_USE_LICENSES] }
    ]
  },
  {
    path: 'customers',
    titleKey: 'area.customers.title',
    icon: 'building-solid',
    requiresProduct: true,
    permissions: [Permission.PORTAL_CAN_USE_MODULE_APPLICATION_ACCOUNTS],
    children: [
      {
        path: 'accounts',
        titleKey: 'customers.accounts.title',
        permissions: [Permission.PORTAL_CAN_USE_APPLICATION_ACCOUNTS]
      },
      /* Across every account: "who can sign in" is asked often enough not to live three clicks deep. */
      {
        path: 'users',
        titleKey: 'customers.users.title',
        permissions: [Permission.PORTAL_CAN_USE_USER_APPLICATION_ACCOUNTS_OVERVIEW]
      }
    ]
  },
  {
    path: 'settings',
    titleKey: 'area.settings.title',
    icon: 'gear-solid',
    requiresProduct: false,
    pinned: true,
    permissions: [Permission.PORTAL_CAN_USE_MODULE_SETTINGS],
    children: [
      /* Subscriptions first, and products live inside that screen rather than beside it: a product
         cannot exist without a subscription and cannot be moved to another one, so a second entry
         would suggest an independence the data model does not have. */
      {
        path: 'subscriptions',
        titleKey: 'settings.subscriptions.title',
        permissions: [Permission.PORTAL_CAN_USE_TENANT_SUBSCRIPTIONS]
      },
      {
        path: 'users',
        titleKey: 'settings.users.title',
        permissions: [Permission.PORTAL_CAN_USE_SYSTEM_USER]
      },
      /* Separate from the users, and not mixed into that list: a server-paginated list cannot absorb
         records from another source without breaking its paging, and an invitation knows little more
         than an email address, so most columns would sit empty.

         **It hangs on the write permission, not on the read one.** An invitation is an offer that was
         sent and can be withdrawn -- there is nothing to read here for someone who may not invite. */
      {
        path: 'invitations',
        titleKey: 'settings.invitations.title',
        permissions: [Permission.PORTAL_CAN_WRITE_SYSTEM_USER]
      }
    ]
  }
];

/** Where an area opens. Its first screen, or the area itself while it has none. */
export function entryPathOf(entry: NavigationEntry): string {
  const first = entry.children?.[0];

  return first === undefined ? entry.path : `${entry.path}/${first.path}`;
}

/**
 * The areas this user may open, with the screens they may open inside them.
 *
 * **An area whose every screen is refused is itself removed.** Someone holding the module permission
 * and none of the screens under it would otherwise see a heading that opens on nothing -- and the
 * area's own route redirects to its first screen, so the entry would lead straight to a refusal.
 *
 * **A pure function over a predicate**, not a method on the permissions service: the sidebar, the
 * refusal page and the start route all need this same answer, and three copies of it would be three
 * chances to filter slightly differently. It takes the predicate rather than the service so it can be
 * tested without an injector.
 */
export function allowedNavigation(
  isGranted: (permissions: readonly Permission[]) => boolean
): readonly NavigationEntry[] {
  return NAVIGATION.filter(area => isGranted(area.permissions))
    .map(area => ({
      ...area,
      children: area.children?.filter(child => isGranted(child.permissions ?? []))
    }))
    .filter(area => area.children === undefined || area.children.length > 0);
}
